import { useMemo, useState, type ReactNode } from 'react';
import { Chevron } from '../Chevron';
import { ConfirmDialog } from '../ConfirmDialog';
import { RefreshError } from '../RefreshError';
import {
  PROXY_ADMIN_BASE,
  modelPath,
  useProxyDiscovery,
  useProxyLabs,
  useProxyModels,
  useProxyWrite,
  type DiscoveryStatus,
  type FetchStatus,
  type ListedModel,
  type ReadPolicy,
  type ReloadResult,
} from '../../hooks/useProxyAdmin';
import {
  ACTIONS_CELL,
  ROW_RULE,
  RowActions,
  TABLE,
  TableScroller,
  Toolbar,
  type View,
} from './AdminTable';
import { FieldDiff, FieldList, JsonBlock } from './ChangePreview';
import { ModelForm } from './ModelForm';
import {
  comparableModel,
  DEFAULT_READ_POLICY,
  describePolicy,
  modelFaults,
} from './modelDraft';

/** What a change to a model's required beta flags means for its callers. */
function BetasNote({ flags }: { flags: string[] | null }) {
  return flags ? (
    <p className="callout callout-warn mt-2">
      Every request to this model must then carry{' '}
      <code>anthropic-beta: {flags.join(',')}</code> (or more): middleman
      refuses one that lacks a flag and never adds it, so callers that don’t
      send them start failing.
    </p>
  ) : (
    <p className="callout mt-2">
      Requests no longer need any <code>anthropic-beta</code> flag.
    </p>
  );
}

/** Reader tags are public to everyone who can see the model. */
function ReaderTagsNote() {
  return (
    <p className="callout callout-warn mt-2">
      Everyone who can see this model sees its reader tags: check none names a
      model, lab or agreement.
    </p>
  );
}

/** Marking a model dead does more than hide it. */
function DeadWarning() {
  return (
    <p className="callout callout-warn mt-2">
      A dead model can’t be called: middleman refuses every request to it and
      Hawk refuses new runs that use it. Its transcripts stay readable.
    </p>
  );
}

type Action =
  | { kind: 'create'; body: Record<string, unknown> }
  | { kind: 'update'; model: ListedModel; patch: Record<string, unknown> }
  | { kind: 'activate' | 'deactivate'; model: ListedModel }
  | { kind: 'reload' };

const faultTexts = (model: ListedModel) =>
  modelFaults(model).map(f => `${f.label}: ${f.message} ${f.effect}`);
const modelText = (model: ListedModel) =>
  `${model.public_name} ${model.group} ${model.lab} ${model.provider ?? ''}`;
// Lists longer than this open by lab rather than all at once.
const GROUP_OVER = 50;

function YesNo({ value }: { value: boolean | null }) {
  return value === null ? (
    <span className="italic text-muted" title="Nobody has recorded it.">
      unknown
    </span>
  ) : value ? (
    <span className="text-ink">yes</span>
  ) : (
    <span className="text-muted">no</span>
  );
}

/** Database-configured models, with the proxy's reload and discovery status. */
export function ProxyModels() {
  const [view, setView] = useState<View>('active');
  const active = useProxyModels('active', view !== 'inactive');
  const inactive = useProxyModels('inactive', view !== 'active');
  const labs = useProxyLabs('active');
  const queries = [
    ...(view !== 'inactive' ? [active] : []),
    ...(view !== 'active' ? [inactive] : []),
  ];
  // Each status has its own query (and so never holds the other's rows as
  // placeholder data, unlike one query whose URL changes).
  const loaded = queries.every(q => q.data);
  const failed = queries.find(q => q.error && !q.data);
  const stale = queries.filter(q => q.error && q.data);
  const rows = useMemo<ListedModel[] | undefined>(() => {
    if (!loaded) return undefined;
    return [
      ...(view !== 'inactive'
        ? (active.data ?? []).map(m => ({ ...m, is_active: true }))
        : []),
      ...(view !== 'active'
        ? (inactive.data ?? []).map(m => ({ ...m, is_active: false }))
        : []),
    ].sort((a, b) => a.public_name.localeCompare(b.public_name));
  }, [loaded, view, active.data, inactive.data]);
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const matches = useMemo(
    () =>
      needle
        ? rows?.filter(m => modelText(m).toLowerCase().includes(needle))
        : rows,
    [rows, needle]
  );

  // Null: no form; 'new': adding a model; else the model being edited.
  const [editing, setEditing] = useState<ListedModel | 'new' | null>(null);
  const [pending, setPending] = useState<Action | null>(null);
  const [reloaded, setReloaded] = useState<ReloadResult | null>(null);
  const { busy, error, send, reset } = useProxyWrite();
  const edit = (next: ListedModel | 'new' | null) => {
    reset();
    setEditing(next);
  };
  const review = (action: Action) => {
    reset();
    setPending(action);
  };

  const confirm = async () => {
    if (!pending) return;
    const result =
      pending.kind === 'create'
        ? await send('POST', `${PROXY_ADMIN_BASE}/models/`, pending.body)
        : pending.kind === 'update'
          ? await send(
              'PATCH',
              modelPath(pending.model.public_name),
              pending.patch
            )
          : pending.kind === 'reload'
            ? await send('POST', `${PROXY_ADMIN_BASE}/models/reload`)
            : await send(
                'POST',
                modelPath(pending.model.public_name, pending.kind)
              );
    if (result === null) return;
    if (pending.kind === 'reload') setReloaded(result as ReloadResult);
    if (pending.kind === 'create' || pending.kind === 'update')
      setEditing(null);
    setPending(null);
  };

  return (
    <section>
      <p className="max-w-3xl text-muted">
        Models configured in the database. Models a provider catalog discovers
        aren’t stored there, so they aren’t listed; <code>hawk models</code>{' '}
        shows the full catalog.
      </p>
      <Toolbar
        view={view}
        onView={setView}
        query={query}
        onQuery={setQuery}
        noun="model"
        count={rows && matches && { shown: matches.length, total: rows.length }}
      >
        <button
          type="button"
          className="btn btn-ghost"
          disabled={busy}
          onClick={() => review({ kind: 'reload' })}
        >
          Reload workers…
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => edit('new')}
        >
          Add model
        </button>
      </Toolbar>

      <Discovery reloaded={reloaded} />

      {editing && (
        <ModelForm
          key={editing === 'new' ? 'new' : editing.public_name}
          model={editing === 'new' ? null : editing}
          labs={labs.data}
          busy={busy}
          onSubmit={body =>
            review(
              editing === 'new'
                ? { kind: 'create', body }
                : { kind: 'update', model: editing, patch: body }
            )
          }
          onCancel={() => edit(null)}
        />
      )}

      <div className="mt-3">
        {failed ? (
          <p className="text-err">{failed.error?.message}</p>
        ) : !rows || !matches ? (
          <p className="text-muted">Loading models…</p>
        ) : (
          <>
            <RefreshError
              error={stale[0]?.error ?? null}
              onRetry={() => stale.forEach(q => void q.refetch())}
            />
            {rows.length === 0 ? (
              <p className="text-muted">
                No {view === 'all' ? '' : `${view} `}models.
              </p>
            ) : matches.length === 0 ? (
              <p className="text-muted">No model matches.</p>
            ) : (
              <ModelTable
                models={matches}
                // A long list opens by lab; a search shows its matches flat.
                grouped={!needle && rows.length > GROUP_OVER}
                busy={busy}
                onEdit={model => edit(model)}
                onToggle={model =>
                  review({
                    kind: model.is_active ? 'deactivate' : 'activate',
                    model,
                  })
                }
              />
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        open={pending !== null}
        title={pending && actionTitle(pending)}
        confirmLabel={pending ? actionLabel(pending) : ''}
        busyLabel="Saving…"
        destructive={pending?.kind === 'deactivate'}
        busy={busy}
        error={pending ? error : null}
        onConfirm={() => void confirm()}
        onClose={() => {
          setPending(null);
          reset();
        }}
      >
        {pending && <ActionPreview action={pending} />}
      </ConfirmDialog>
    </section>
  );
}

function actionTitle(action: Action): ReactNode {
  const name = (text: string) => (
    <span className="font-mono text-xs font-normal text-ink-2">{text}</span>
  );
  switch (action.kind) {
    case 'create':
      return <>Add model {name(String(action.body.public_name))}</>;
    case 'update':
      return <>Change model {name(action.model.public_name)}</>;
    case 'activate':
      return <>Activate model {name(action.model.public_name)}</>;
    case 'deactivate':
      return <>Deactivate model {name(action.model.public_name)}</>;
    case 'reload':
      return 'Reload proxy workers';
  }
}

function actionLabel(action: Action): string {
  return {
    create: 'Add model',
    update: 'Save changes',
    activate: 'Activate',
    deactivate: 'Deactivate',
    reload: 'Reload',
  }[action.kind];
}

function ActionPreview({ action }: { action: Action }) {
  switch (action.kind) {
    case 'create':
      return (
        <>
          <p>
            Adds the model in group{' '}
            <code className="text-ink">{String(action.body.group)}</code> on lab{' '}
            <code className="text-ink">{String(action.body.lab)}</code>.
            {action.body.dead === true
              ? ' Marked Dead, so nobody can call it yet.'
              : ' Callers with that group can use it as soon as the proxy reloads.'}
          </p>
          {action.body.dead === true && <DeadWarning />}
          <p className="mt-2">
            Readers allowed to read its non-public transcripts:{' '}
            <b>
              {describePolicy(
                (action.body.read_policy as ReadPolicy | undefined) ??
                  DEFAULT_READ_POLICY
              )}
            </b>
            .
          </p>
          {'reader_tags' in action.body && <ReaderTagsNote />}
          {'required_anthropic_betas' in action.body && (
            <BetasNote
              flags={action.body.required_anthropic_betas as string[]}
            />
          )}
          <JsonBlock value={action.body} label="Request body" />
        </>
      );
    case 'update':
      return (
        <FieldDiff before={comparableModel(action.model)} patch={action.patch}>
          {'group' in action.patch && (
            <p className="callout callout-warn mt-2">
              Moving a model to another group changes who can call it. Runs
              already recorded keep the groups captured when they ran.
            </p>
          )}
          {action.patch.are_details_secret === false &&
            action.model.are_details_secret && (
              <p className="callout callout-warn mt-2">
                The model’s upstream name and details stop being secret.
              </p>
            )}
          {action.patch.dead === true && <DeadWarning />}
          {'read_policy' in action.patch && (
            <p className="callout mt-2">
              Readers allowed to read its non-public transcripts change from{' '}
              <b>{describePolicy(action.model.read_policy)}</b> to{' '}
              <b>
                {describePolicy(
                  (action.patch.read_policy as ReadPolicy | null) ??
                    DEFAULT_READ_POLICY
                )}
              </b>
              . Transcripts already read stay where they are.
            </p>
          )}
          {'reader_tags' in action.patch && <ReaderTagsNote />}
          {'required_anthropic_betas' in action.patch && (
            <BetasNote
              flags={action.patch.required_anthropic_betas as string[] | null}
            />
          )}
          {modelFaults(action.model)
            .filter(f => f.field in action.patch)
            .map(f => (
              <p key={f.field} className="callout mt-2">
                Rewrites the malformed stored {f.label.toLowerCase()} (shown
                above as middleman’s cleaned value) with the new one.
              </p>
            ))}
        </FieldDiff>
      );
    case 'deactivate':
      return (
        <>
          <p>
            Middleman stops loading this configuration (it’s kept, so it can be
            activated again).
          </p>
          <p className="callout callout-warn mt-2">
            To retire a model, mark it <b>Dead</b> instead (Edit → Dead): that
            keeps its configuration and group and refuses new calls.
            Deactivating either removes it, or leaves it public:
          </p>
          <ul className="mt-1 list-disc pl-5">
            <li>
              If no provider catalog offers{' '}
              <code>{action.model.public_name}</code>, the model disappears:
              requests and group lookups for it fail, so Hawk can’t resolve its
              group when importing, scanning or model-searching old data that
              uses it.
            </li>
            <li>
              If a catalog does offer it, middleman keeps serving it from the
              catalog, in <code>model-access-public</code> rather than{' '}
              <code>{action.model.group}</code>.
            </li>
          </ul>
        </>
      );
    case 'activate':
      return (
        <>
          <p>
            Middleman loads its configuration again. Refused unless its lab{' '}
            <code className="text-ink">{action.model.lab}</code> is active.
          </p>
          {action.model.dead ? (
            // Activation leaves `dead` as it is (middleman only sets is_active).
            <p className="callout callout-warn mt-2">
              It stays <b>Dead</b>, so calls are still refused: clear Dead (Edit
              → Dead) for callers with group <code>{action.model.group}</code>{' '}
              to use it again.
            </p>
          ) : (
            <p className="mt-2">
              Callers with group{' '}
              <code className="text-ink">{action.model.group}</code> can use it
              again.
            </p>
          )}
        </>
      );
    case 'reload':
      return (
        <p>
          Reloads provider keys and model configs and reruns provider discovery.
          With the shared Valkey cache, every worker reloads (as after any write
          here); use this after changing the database directly or to retry a
          failed discovery. Without Valkey, only the worker that answers
          reloads, and the others pick up changes at their hourly refresh:
          restart middleman to apply a change everywhere now.
        </p>
      );
  }
}

const STATUS_CLASS: Record<FetchStatus, string> = {
  ok: 'text-ok',
  cached: 'text-warn',
  stale: 'text-warn',
  failed: 'text-err',
  no_key: 'text-muted',
  not_run: 'text-muted',
};
const STATUS_HINT: Record<FetchStatus, string> = {
  ok: 'Fetched',
  cached: 'Upstream failed; serving the disk cache',
  stale: 'Reload failed before refetching; models keep the old list',
  failed: 'Upstream failed with no cache',
  no_key: 'No provider key set; skipped',
  not_run: 'Not run yet',
};

/** Each provider's last discovery outcome, from one replica. */
function Discovery({ reloaded }: { reloaded: ReloadResult | null }) {
  const discovery = useProxyDiscovery();
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3">
      <button
        type="button"
        className="row-action inline-flex items-center gap-1.5"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Chevron open={open} size={16} />
        Discovery
        {typeof discovery.data?.model_count === 'number' && (
          <span className="text-muted">
            · {discovery.data.model_count.toLocaleString()}{' '}
            {discovery.data.model_count === 1 ? 'model' : 'models'} loaded
          </span>
        )}
      </button>
      {reloaded && (
        <div role="status" className="callout mt-2 max-w-3xl">
          Reloaded: {reloaded.model_count?.toLocaleString() ?? 'unknown'}{' '}
          models.
          <DiscoveryList discovery={reloaded.discovery} />
        </div>
      )}
      {open && (
        <div className="mt-1 max-w-3xl pl-6">
          <p className="text-muted">
            Answered by one proxy replica, so it can differ between refreshes.{' '}
            <button
              type="button"
              className="row-action"
              disabled={discovery.isFetching}
              onClick={() => void discovery.refetch()}
            >
              {discovery.isFetching ? 'Refreshing…' : 'Refresh'}
            </button>
          </p>
          {discovery.error && !discovery.data ? (
            <p className="text-err">{discovery.error.message}</p>
          ) : discovery.data ? (
            <DiscoveryList discovery={discovery.data.discovery} />
          ) : (
            <p className="text-muted">Loading…</p>
          )}
        </div>
      )}
    </div>
  );
}

function DiscoveryList({
  discovery,
}: {
  /** Absent when the reply didn't carry it. */
  discovery: DiscoveryStatus['discovery'] | undefined;
}) {
  const entries = Object.entries(discovery ?? {}).sort(([a], [b]) =>
    a.localeCompare(b)
  );
  if (entries.length === 0)
    return <p className="mt-1 text-muted">No providers report discovery.</p>;
  return (
    <ul className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
      {entries.map(([provider, status]) => (
        <li key={provider} title={STATUS_HINT[status] ?? status}>
          <span className="font-mono text-ink">{provider}</span>{' '}
          <span className={STATUS_CLASS[status] ?? ''}>{status}</span>
        </li>
      ))}
    </ul>
  );
}

const COLUMNS = 8;

function ModelTable({
  models,
  grouped,
  busy,
  onEdit,
  onToggle,
}: {
  models: ListedModel[];
  /** Open by lab: one collapsible row per lab, its models under it. */
  grouped: boolean;
  busy: boolean;
  onEdit: (model: ListedModel) => void;
  onToggle: (model: ListedModel) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const [openLabs, setOpenLabs] = useState<ReadonlySet<string>>(new Set());
  const labs = useMemo(
    () =>
      [...Map.groupBy(models, m => m.lab)].sort(
        (a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0])
      ),
    [models]
  );
  const toggleLab = (lab: string) =>
    setOpenLabs(prev => {
      const next = new Set(prev);
      if (!next.delete(lab)) next.add(lab);
      return next;
    });
  // The labs listed now: openLabs can still hold names an edit or refetch removed.
  const allOpen = labs.every(([lab]) => openLabs.has(lab));
  const rows = (list: ListedModel[]) =>
    list.map(model => (
      <ModelRow
        key={model.public_name}
        model={model}
        open={open === model.public_name}
        onExpand={() =>
          setOpen(open === model.public_name ? null : model.public_name)
        }
        busy={busy}
        onEdit={() => onEdit(model)}
        onToggle={() => onToggle(model)}
      />
    ));

  return (
    <>
      {grouped && (
        <p className="text-muted">
          Grouped by lab.{' '}
          <button
            type="button"
            className="row-action"
            onClick={() =>
              setOpenLabs(allOpen ? new Set() : new Set(labs.map(([l]) => l)))
            }
          >
            {allOpen ? 'Collapse all' : 'Expand all'}
          </button>
        </p>
      )}
      <TableScroller>
        <table className={TABLE}>
          <thead className="eyebrow">
            <tr>
              <th className="py-1.5 pr-4 font-semibold">Model</th>
              <th className="px-2 font-semibold">Group</th>
              <th className="px-2 font-semibold">Lab</th>
              <th className="px-2 font-semibold">Provider</th>
              <th className="px-2 font-semibold" title="Zero data retention">
                ZDR
              </th>
              <th className="px-2 font-semibold">Raw CoT</th>
              <th className="px-2 font-semibold">Flags</th>
              <th className={ACTIONS_CELL}>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          {grouped ? (
            labs.map(([lab, list]) => (
              <tbody key={lab}>
                <tr className={`bg-surface-2 ${ROW_RULE}`}>
                  <td colSpan={COLUMNS} className="py-1.5 pr-4">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 text-left text-xs font-semibold text-ink hover:underline"
                      aria-expanded={openLabs.has(lab)}
                      onClick={() => toggleLab(lab)}
                    >
                      <Chevron open={openLabs.has(lab)} size={16} />
                      <span className="font-mono">{lab}</span>
                      <span className="font-normal tabular-nums text-muted">
                        {list.length.toLocaleString()}{' '}
                        {list.length === 1 ? 'model' : 'models'}
                      </span>
                    </button>
                  </td>
                </tr>
                {openLabs.has(lab) && rows(list)}
              </tbody>
            ))
          ) : (
            <tbody>{rows(models)}</tbody>
          )}
        </table>
      </TableScroller>
    </>
  );
}

function ModelRow({
  model,
  open,
  onExpand,
  busy,
  onEdit,
  onToggle,
}: {
  model: ListedModel;
  open: boolean;
  onExpand: () => void;
  busy: boolean;
  onEdit: () => void;
  onToggle: () => void;
}) {
  const faults = faultTexts(model);
  const { is_active: _active, ...config } = model;
  return (
    <>
      <tr className={`align-middle ${ROW_RULE}`}>
        <td className="py-1.5 pr-4">
          <button
            type="button"
            className="row-action inline-flex items-center gap-1.5 text-left font-mono text-ink"
            aria-expanded={open}
            onClick={onExpand}
          >
            <Chevron open={open} size={16} />
            {model.public_name}
          </button>
        </td>
        <td className="px-2 font-mono">{model.group}</td>
        <td className="px-2 font-mono">{model.lab}</td>
        <td className="px-2">
          {model.provider ?? <span className="italic text-muted">unknown</span>}
        </td>
        <td className="px-2">
          <YesNo value={model.is_zdr} />
        </td>
        <td className="px-2">
          <YesNo value={model.raw_cot} />
        </td>
        <td className="space-x-1 px-2">
          {!model.is_active && <span className="badge">inactive</span>}
          {model.dead && <span className="badge">dead</span>}
          {model.vision && <span className="badge">vision</span>}
          {model.required_anthropic_betas && (
            <span
              className="badge"
              title={`Requests must carry anthropic-beta: ${model.required_anthropic_betas.join(', ')}`}
            >
              betas
            </span>
          )}
          {model.are_details_secret && <span className="badge">secret</span>}
          {faults.length > 0 && (
            <span className="badge text-warn" title={faults.join('\n')}>
              config fault
            </span>
          )}
        </td>
        <td className={ACTIONS_CELL}>
          <RowActions
            name={model.public_name}
            active={model.is_active}
            busy={busy}
            onEdit={onEdit}
            onToggle={onToggle}
          />
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={COLUMNS} className="whitespace-normal pb-3 pl-6">
            {faults.map(fault => (
              <p key={fault} className="callout callout-warn mb-2 max-w-3xl">
                {fault}
              </p>
            ))}
            <FieldList record={config} />
          </td>
        </tr>
      )}
    </>
  );
}
